import { CONNECTOR_LIMITS } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Checkbox, Field, Input } from "@sagot/ui";
import type { ConnectorCallStep } from "../types";
import { OPERATION_EXAMPLES, outputProblems, outputSuggestion, outputTarget, withOutput } from "./connector-op-model";

const C = "engage.workflows.connector.op.outputs";

interface OutputsProps {
  step: ConnectorCallStep;
  /** The variables the operation's response mapping declares. */
  declared: readonly string[];
  disabled: boolean;
  onChange: (step: ConnectorCallStep) => void;
}

/** The operation's declared outputs and the run variable each one is copied into (its own name unless the author renames it). */
export function OperationOutputs({ step, declared, disabled, onChange }: OutputsProps) {
  const { t } = useTranslation();
  const problems = outputProblems(step);
  return (
    <fieldset className="space-y-3" data-testid="op-outputs">
      <legend className="label">{t(`${C}.title`)}</legend>
      <p className="hint -mt-1">{t(`${C}.hint`, { example: OPERATION_EXAMPLES.output })}</p>
      {declared.length === 0 ? <p className="rounded-md bg-surface-2 p-3 text-xs text-fg-muted" data-testid="op-outputs-none">{t(`${C}.none`)}</p> : null}
      {declared.map((name) => {
        const target = outputTarget(step, name);
        const problem = problems[name];
        return (
          <div key={name} className="grid gap-2 sm:grid-cols-[minmax(0,12rem)_1fr] sm:items-start" data-testid={`op-output-${name}`}>
            <Checkbox
              checked={target !== null}
              disabled={disabled}
              label={<code className="font-mono text-xs font-semibold">{name}</code>}
              onCheckedChange={(checked) => { onChange(withOutput(step, name, checked === true ? outputSuggestion(name) : null, declared)); }}
            />
            <Field label={t(`${C}.into`)} error={problem ? t(`${C}.problems.${problem}`) : undefined}>
              <Input
                className="font-mono text-xs"
                value={target ?? ""}
                disabled={disabled || target === null}
                maxLength={CONNECTOR_LIMITS.variableName + 4}
                aria-label={t(`${C}.intoFor`, { name })}
                onChange={(event) => { onChange(withOutput(step, name, event.target.value, declared)); }}
              />
            </Field>
          </div>
        );
      })}
    </fieldset>
  );
}
